import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from '../components/AdminLayout';

interface SettingItem {
  id?: string;
  key: string;
  value: string;
  description: string;
}

const defaultSettings: SettingItem[] = [
  { key: 'event_name', value: 'Sona Nalam Nova 2026', description: 'Event name displayed across the platform' },
  { key: 'hosted_by', value: 'Sona College of Technology', description: 'Host institution name' },
  { key: 'association', value: 'Indian Medical Association (IMA), Salem', description: 'Partner association' },
  { key: 'venue', value: 'Sona College of Technology, Salem', description: 'Event venue address' },
  { key: 'registration_fee', value: '2000', description: 'Registration fee per team (in INR)' },
  { key: 'max_team_size', value: '4', description: 'Maximum participants per team' },
  { key: 'min_team_size', value: '3', description: 'Minimum participants per team' },
  { key: 'registration_deadline', value: '2026-09-01', description: 'Registration deadline date' },
  { key: 'event_dates', value: '2026-09-25 to 2026-09-26', description: 'Event dates' },
  { key: 'registration_enabled', value: 'true', description: 'Enable/disable new registrations' },
  { key: 'payments_enabled', value: 'true', description: 'Enable/disable payment gateway' },
  { key: 'faculty_mentor_enabled', value: 'true', description: 'Enable/disable faculty mentor requirement' },
  { key: 'problem_statements_published', value: 'false', description: 'Publish/unpublish problem statements' },
  { key: 'razorpay_key_id', value: '', description: 'Razorpay Key ID (public, for frontend checkout)' },
  { key: 'prize_pool', value: '500000', description: 'Total prize pool amount in INR' },
  { key: 'prize_currency', value: 'INR', description: 'Prize pool currency code' },
  { key: 'prize_display_text', value: '₹5,00,000', description: 'Display text for prize pool on homepage' },
  { key: 'convenience_fee_type', value: 'percentage', description: 'Convenience fee type: fixed or percentage' },
  { key: 'convenience_fee_value', value: '2.5', description: 'Convenience fee value (INR or percentage)' },
  { key: 'gst_enabled', value: 'false', description: 'Enable GST on payments' },
  { key: 'gst_percentage', value: '18', description: 'GST percentage' },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState<SettingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase.from('settings').select('*');
      if (error) throw error;

      const merged: SettingItem[] = defaultSettings.map((def) => {
        const existing = data?.find((d: SettingItem) => d.key === def.key);
        return existing ? { ...def, id: existing.id, value: existing.value } : def;
      });

      for (const setting of merged) {
        if (!setting.id) {
          await supabase.from('settings').upsert({ key: setting.key, value: setting.value, description: setting.description }, { onConflict: 'key' });
        }
      }

      setSettings(merged);
    } catch {
      setSaveError('Failed to load settings');
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (key: string, value: string) => {
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value } : s)));
    setSaveMessage('');
    setSaveError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage('');
    setSaveError('');

    try {
      const upserts = settings.map((s) => ({ key: s.key, value: s.value, description: s.description }));
      for (const s of upserts) {
        const { error } = await supabase.from('settings').upsert(s, { onConflict: 'key' });
        if (error) throw error;
      }
      setSaveMessage('Settings saved successfully!');
    } catch {
      setSaveError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getSetting = (key: string) => settings.find((s) => s.key === key);

  if (loading) {
    return (
      <AuthGuard requiredRole="admin">
        <AdminLayout title="Settings" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Settings' }]}>
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
          </div>
        </AdminLayout>
      </AuthGuard>
    );
  }

  const tabs = [
    { id: 'general', label: 'General', icon: 'ri-settings-3-line' },
    { id: 'registration', label: 'Registration', icon: 'ri-file-list-3-line' },
    { id: 'payment', label: 'Payment', icon: 'ri-bank-card-line' },
    { id: 'prize', label: 'Prize Pool', icon: 'ri-trophy-line' },
    { id: 'fees', label: 'Convenience Fee', icon: 'ri-money-dollar-circle-line' },
  ];

  return (
    <AuthGuard requiredRole="admin">
      <AdminLayout title="Settings" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Settings' }]}>
        {(saveMessage || saveError) && (
          <div className={`mb-6 p-4 rounded-xl text-sm ${saveMessage ? 'bg-accent-50 border border-accent-200 text-accent-800' : 'bg-red-50 border border-red-200 text-red-600'}`}>
            {saveMessage || saveError}
          </div>
        )}

        <div className="flex items-center gap-1 mb-6 p-1 bg-background-100 rounded-full w-fit overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab.id ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'general' && (
          <div className="space-y-6">
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-1">Event Information</h2>
              <p className="text-sm text-foreground-500 mb-6">Basic event details</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Event Name</label>
                  <input type="text" value={getSetting('event_name')?.value || ''} onChange={(e) => updateSetting('event_name', e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Hosted By</label>
                  <input type="text" value={getSetting('hosted_by')?.value || ''} onChange={(e) => updateSetting('hosted_by', e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Association</label>
                  <input type="text" value={getSetting('association')?.value || ''} onChange={(e) => updateSetting('association', e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Venue</label>
                  <input type="text" value={getSetting('venue')?.value || ''} onChange={(e) => updateSetting('venue', e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Event Dates</label>
                  <input type="text" value={getSetting('event_dates')?.value || ''} onChange={(e) => updateSetting('event_dates', e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" placeholder="2026-09-25 to 2026-09-26" />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'registration' && (
          <div className="space-y-6">
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-1">Registration Settings</h2>
              <p className="text-sm text-foreground-500 mb-6">Manage registration parameters</p>
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">Registration Fee (₹)</label>
                    <input type="number" value={getSetting('registration_fee')?.value || '2000'} onChange={(e) => updateSetting('registration_fee', e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">Max Team Size</label>
                    <input type="number" value={getSetting('max_team_size')?.value || '4'} onChange={(e) => updateSetting('max_team_size', e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">Min Team Size</label>
                    <input type="number" value={getSetting('min_team_size')?.value || '3'} onChange={(e) => updateSetting('min_team_size', e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Registration Deadline</label>
                  <input type="date" value={getSetting('registration_deadline')?.value || '2026-09-01'} onChange={(e) => updateSetting('registration_deadline', e.target.value)} className="w-full max-w-xs px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2">
                  {[
                    { key: 'registration_enabled', label: 'Enable Registrations', desc: 'Allow new team registrations' },
                    { key: 'payments_enabled', label: 'Enable Payments', desc: 'Allow payment processing' },
                    { key: 'faculty_mentor_enabled', label: 'Require Faculty Mentor', desc: 'Make faculty mentor mandatory' },
                    { key: 'problem_statements_published', label: 'Publish Problem Statements', desc: 'Visible to paid participants' },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between p-4 rounded-xl bg-background-100/70">
                      <div>
                        <span className="text-sm font-medium text-foreground-700">{item.label}</span>
                        <p className="text-xs text-foreground-400">{item.desc}</p>
                      </div>
                      <button
                        onClick={() => {
                          const current = getSetting(item.key)?.value === 'true';
                          updateSetting(item.key, current ? 'false' : 'true');
                        }}
                        className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${getSetting(item.key)?.value === 'true' ? 'bg-accent-500' : 'bg-background-300'}`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${getSetting(item.key)?.value === 'true' ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'payment' && (
          <div className="space-y-6">
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-1">Payment Gateway</h2>
              <p className="text-sm text-foreground-500 mb-6">Razorpay configuration</p>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Razorpay Key ID</label>
                <input type="text" value={getSetting('razorpay_key_id')?.value || ''} onChange={(e) => updateSetting('razorpay_key_id', e.target.value)} placeholder="rzp_live_xxxxxxxxxxxxx" className="w-full max-w-md px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors font-mono" />
                <p className="text-xs text-foreground-400 mt-1">Your Razorpay public Key ID. The Key Secret must be configured in the Supabase Dashboard as RAZORPAY_KEY_SECRET.</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'prize' && (
          <div className="space-y-6">
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-1">Prize Pool Configuration</h2>
              <p className="text-sm text-foreground-500 mb-6">Configure the prize pool displayed on the homepage</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Total Prize Pool (INR)</label>
                  <input type="number" value={getSetting('prize_pool')?.value || '500000'} onChange={(e) => updateSetting('prize_pool', e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Currency</label>
                  <input type="text" value={getSetting('prize_currency')?.value || 'INR'} onChange={(e) => updateSetting('prize_currency', e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Display Text (shown on homepage)</label>
                  <input type="text" value={getSetting('prize_display_text')?.value || '₹5,00,000'} onChange={(e) => updateSetting('prize_display_text', e.target.value)} className="w-full max-w-md px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" placeholder="e.g. ₹5,00,000" />
                  <p className="text-xs text-foreground-400 mt-1">This text appears in the Hero section on the homepage. You can format it as you like.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'fees' && (
          <div className="space-y-6">
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-1">Convenience Fee Configuration</h2>
              <p className="text-sm text-foreground-500 mb-6">Configure additional fees charged during payment</p>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-2">Fee Type</label>
                  <div className="flex gap-3">
                    {['fixed', 'percentage'].map((type) => (
                      <button
                        key={type}
                        onClick={() => updateSetting('convenience_fee_type', type)}
                        className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${getSetting('convenience_fee_type')?.value === type ? 'bg-primary-500 text-background-50 dark:text-foreground-950' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
                      >
                        {type === 'fixed' ? 'Fixed Amount (₹)' : 'Percentage (%)'}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {getSetting('convenience_fee_type')?.value === 'fixed' ? 'Fee Amount (₹)' : 'Fee Percentage (%)'}
                  </label>
                  <input
                    type="number"
                    value={getSetting('convenience_fee_value')?.value || '2.5'}
                    onChange={(e) => updateSetting('convenience_fee_value', e.target.value)}
                    step="0.1"
                    className="w-32 px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors"
                  />
                  <p className="text-xs text-foreground-400 mt-1">
                    {getSetting('convenience_fee_type')?.value === 'fixed'
                      ? 'Fixed amount added to each payment'
                      : 'Percentage of registration fee added as convenience charge'}
                  </p>
                </div>
                <div className="pt-2 border-t border-background-200/70">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-3">GST Configuration</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 rounded-xl bg-background-100/70">
                      <div>
                        <span className="text-sm font-medium text-foreground-700">Enable GST</span>
                        <p className="text-xs text-foreground-400">Add GST to payments</p>
                      </div>
                      <button
                        onClick={() => updateSetting('gst_enabled', getSetting('gst_enabled')?.value === 'true' ? 'false' : 'true')}
                        className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${getSetting('gst_enabled')?.value === 'true' ? 'bg-accent-500' : 'bg-background-300'}`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${getSetting('gst_enabled')?.value === 'true' ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                      </button>
                    </div>
                    {getSetting('gst_enabled')?.value === 'true' && (
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">GST Percentage (%)</label>
                        <input
                          type="number"
                          value={getSetting('gst_percentage')?.value || '18'}
                          onChange={(e) => updateSetting('gst_percentage', e.target.value)}
                          className="w-32 px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            {saving && <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin"></div>}
            {saving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      </AdminLayout>
    </AuthGuard>
  );
}